import { dateLib, roomLib, tenantLib } from "@agentcall/core/lib"
import type { PlanRes, RoomRes, TenantRes } from "@agentcall/core/res"
import { fileType, tenantType } from "@agentcall/core/type"
import { Suspense, useEffect, useState } from "react"
import { BookOpen, Edit2, Plus } from "react-feather"
import { useLocation } from "react-router"
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import { uploadFiles } from "@/api/client/file"
import { planApi } from "@/api/client/plan"
import { roomApi } from "@/api/client/room"
import { tenantApi } from "@/api/client/tenant"
import { getErrorMessage } from "@/api/instance"
import { EchoesEditGuard } from "@/components/echoes/EchoesEditGuard"
import { PaymentMethodRequiredDialog } from "@/components/payment/PaymentMethodRequiredDialog"
import { OwnerMemoSectionContent } from "@/components/properties/owner-memo"
import { PropertyHeader } from "@/components/properties/PropertyHeader"
import { AttachmentModal } from "@/components/room/AttachmentModal"
import { EndDateChangeDialog } from "@/components/room/EndDateChangeDialog"
import {
  RoomPlanInfoCard,
  RoomRelatedCasesSection,
  RoomTenantInfoCard,
} from "@/components/room/RoomDetailCards"
import { RoomRelatedInfo } from "@/components/room/RoomRelatedInfo"
import { TenantStatusBadge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, SummaryCard } from "@/components/ui/card"
import { Loading } from "@/components/ui/loading"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs/tabs"
import { usePaymentMethodRequirement } from "@/hooks/usePaymentMethodRequirement"

const EMPTY_RENTAL_CONTRACT_ATTACHMENT_ID = "empty-rental-contract"
const EMPTY_IMPORTANT_MATTERS_ATTACHMENT_ID = "empty-important-matters"

export function RoomDetailPage() {
  const navigate = useNavigate()
  const { ensurePaymentMethod, isPaymentMethodDialogOpen, setIsPaymentMethodDialogOpen } =
    usePaymentMethodRequirement()
  const location = useLocation()
  const { propertyId: propertyIdParam, roomId: roomIdParam } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const parsedPropertyId = Number(propertyIdParam)
  const propertyId = Number.isFinite(parsedPropertyId) ? parsedPropertyId : undefined
  const parsedRoomId = Number(roomIdParam)
  const roomId = Number.isFinite(parsedRoomId) ? parsedRoomId : undefined
  const hasValidIds = propertyId !== undefined && roomId !== undefined
  const tabParam = searchParams.get("tab")
  const [activeTab, setActiveTab] = useState(tabParam === "related" ? "related" : "details")
  const [attachmentModalOpen, setAttachmentModalOpen] = useState(false)
  const [attachmentModalMode, setAttachmentModalMode] = useState<"add" | "edit">("add")
  const [selectedAttachment, setSelectedAttachment] = useState<{
    id: string
    title: string
    imageUrl?: string
    fileName?: string | null
    fileType?: string | null
    index?: number
  } | null>(null)
  const [isRevivalDialogOpen, setIsRevivalDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [showAddMemoModal, setShowAddMemoModal] = useState(false)
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false)
  const [endDateChangeTarget, setEndDateChangeTarget] = useState<
    { type: "tenant"; value: TenantRes } | { type: "plan"; value: PlanRes } | null
  >(null)

  useEffect(() => {
    const tab = searchParams.get("tab")
    if (tab === "details" || tab === "related") {
      setActiveTab(tab)
    }
  }, [searchParams])

  const handleTabChange = (value: string) => {
    setActiveTab(value)
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set("tab", value)
      return next
    })
  }

  const {
    data: roomResponse,
    isLoading: isRoomLoading,
    error: roomError,
    refetch: refetchRoom,
  } = roomApi.useRoomGet(
    roomId,
    {
      withProperty: "true",
      withTenants: "true",
      withTenantsFiles: "true",
      withTenantsOccupants: "true",
      withPlans: "true",
    },
    {
      query: {
        enabled: roomId !== undefined,
      },
    }
  )

  useEffect(() => {
    if (location.state?.shouldRefetch) {
      refetchRoom()
    }
  }, [location.state, refetchRoom])

  const { mutate: reviveTenant, isPending: isReviving } = tenantApi.useTenantRevival({
    mutation: {
      onSuccess: () => {
        toast.success("退去を取り消しました")
        setIsRevivalDialogOpen(false)
        refetchRoom()
      },
      onError: (error) => {
        console.error("Failed to revive tenant:", error)
        toast.error("退去の取り消しに失敗しました", { description: getErrorMessage(error) })
      },
    },
  })

  const { mutate: deleteTenant, isPending: isDeleting } = tenantApi.useTenantDelete({
    mutation: {
      onSuccess: () => {
        toast.success("賃借人を削除しました")
        setIsDeleteDialogOpen(false)
        refetchRoom()
      },
      onError: (error) => {
        console.error("Failed to delete tenant:", error)
        toast.error("賃借人の削除に失敗しました", { description: getErrorMessage(error) })
      },
    },
  })

  const { mutate: revivePlan, isPending: isPlanReviving } = planApi.usePlanRevival({
    mutation: {
      onSuccess: () => {
        toast.success("プラン終了を取り消しました")
        refetchRoom()
      },
      onError: (error) => {
        console.error("Failed to revive plan:", error)
        toast.error("プラン終了の取り消しに失敗しました", { description: getErrorMessage(error) })
      },
    },
  })

  const tenantEndDateChangeMutation = tenantApi.useTenantCancel({
    mutation: {
      onSuccess: () => {
        toast.success("退去日を変更しました")
        setEndDateChangeTarget(null)
        refetchRoom()
      },
      onError: (error) => {
        toast.error("退去日の変更に失敗しました", { description: getErrorMessage(error) })
      },
    },
  })

  const planEndDateChangeMutation = planApi.usePlanCancel({
    mutation: {
      onSuccess: () => {
        toast.success("終了日を変更しました")
        setEndDateChangeTarget(null)
        refetchRoom()
      },
      onError: (error) => {
        toast.error("終了日の変更に失敗しました", { description: getErrorMessage(error) })
      },
    },
  })

  const { mutate: deletePlan, isPending: isPlanDeleting } = planApi.usePlanDelete({
    mutation: {
      onSuccess: () => {
        toast.success("プランを削除しました")
        refetchRoom()
      },
      onError: (error) => {
        console.error("Failed to delete plan:", error)
        toast.error("プランの削除に失敗しました", { description: getErrorMessage(error) })
      },
    },
  })

  const { mutateAsync: updateTenant } = tenantApi.useTenantUpdate({
    mutation: {
      onSuccess: () => {
        refetchRoom()
      },
      onError: (error) => {
        console.error("Failed to update tenant:", error)
        toast.error("添付ファイルの更新に失敗しました", { description: getErrorMessage(error) })
      },
    },
  })

  if (!hasValidIds) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <div className="text-slate-600">URLに含まれる物件IDまたは部屋IDが正しくありません。</div>
        <Button onClick={() => navigate("/properties")} variant="outline">
          物件一覧に戻る
        </Button>
      </div>
    )
  }

  const room = roomResponse?.room

  const activeTenant = roomLib.roomActiveTenant(room)
  const plannedTenant = roomLib.roomPlannedTenant(room)
  const currentTenant = roomLib.roomCurrentTenant(room)
  // 加入中のプランがあるときだけ、開始前のプランを「次のプラン」として別に出す
  const nextTenant = plannedTenant

  const handleAddAttachment = () => {
    setAttachmentModalMode("add")
    setSelectedAttachment(null)
    setAttachmentModalOpen(true)
  }

  const handleEditAttachment = (id: string) => {
    if (
      id === EMPTY_RENTAL_CONTRACT_ATTACHMENT_ID ||
      id === EMPTY_IMPORTANT_MATTERS_ATTACHMENT_ID
    ) {
      const isRentalContract = id === EMPTY_RENTAL_CONTRACT_ATTACHMENT_ID
      setAttachmentModalMode("add")
      setSelectedAttachment({
        id,
        title: isRentalContract
          ? fileType.TENANT_FILE_TITLE_RENTAL_CONTRACT
          : fileType.TENANT_FILE_TITLE_IMPORTANT_MATTERS,
        index: isRentalContract ? 0 : 1,
      })
      setAttachmentModalOpen(true)
      return
    }

    const fileIndex = currentTenant?.files?.findIndex((f) => String(f.id) === id) ?? -1
    const file = fileIndex >= 0 ? currentTenant?.files?.[fileIndex] : undefined
    if (file) {
      const fixedFileIndex =
        file.tenantFileType === fileType.TENANT_FILE_TYPE_RENTAL_CONTRACT
          ? 0
          : file.tenantFileType === fileType.TENANT_FILE_TYPE_IMPORTANT_MATTERS
            ? 1
            : undefined
      setAttachmentModalMode("edit")
      setSelectedAttachment({
        id: String(file.id),
        title: file.title ?? file.fileName ?? "",
        imageUrl: file.url,
        fileName: file.fileName,
        fileType: file.fileType,
        index: fixedFileIndex,
      })
      setAttachmentModalOpen(true)
    }
  }

  const handleAttachmentSubmit = async (data: { title: string; file?: File }) => {
    if (!currentTenant || !roomId) {
      toast.error("賃借人情報が見つかりません")
      return
    }

    setIsUploadingAttachment(true)
    try {
      // 新規ファイルがあればS3にアップロード
      let uploadedFiles: {
        path: string
        fileName: string
        title: string
        tenantFileType: fileType.tenantFileType
      }[] = []
      if (data.file) {
        const uploadResults = await uploadFiles([data.file])
        // 追加時はその他、編集時は既存のファイルタイプを保持する
        const selectedFixedTenantFileType =
          selectedAttachment?.index === 0
            ? fileType.TENANT_FILE_TYPE_RENTAL_CONTRACT
            : selectedAttachment?.index === 1
              ? fileType.TENANT_FILE_TYPE_IMPORTANT_MATTERS
              : undefined
        let tenantFileTypeValue: fileType.tenantFileType =
          selectedFixedTenantFileType ?? fileType.TENANT_FILE_TYPE_OTHER_IMAGE
        if (!selectedFixedTenantFileType && attachmentModalMode === "edit" && selectedAttachment) {
          // 編集時は既存のファイルタイプを保持
          const existingFile = currentTenant.files?.find(
            (f) => String(f.id) === selectedAttachment.id
          )
          tenantFileTypeValue =
            (existingFile?.tenantFileType as fileType.tenantFileType) ??
            fileType.TENANT_FILE_TYPE_OTHER_IMAGE
        }
        // タイトルは1,2枚目は固定、それ以外はユーザー入力
        const title =
          tenantFileTypeValue === fileType.TENANT_FILE_TYPE_RENTAL_CONTRACT
            ? fileType.TENANT_FILE_TITLE_RENTAL_CONTRACT
            : tenantFileTypeValue === fileType.TENANT_FILE_TYPE_IMPORTANT_MATTERS
              ? fileType.TENANT_FILE_TITLE_IMPORTANT_MATTERS
              : data.title
        uploadedFiles = uploadResults.map((result) => ({
          path: result.key,
          fileName: result.fileName,
          title,
          tenantFileType: tenantFileTypeValue,
        }))
      }

      // 編集モードで新しいファイルをアップロードした場合、古いファイルを削除
      const deleteFileIds: number[] = []
      if (attachmentModalMode === "edit" && selectedAttachment && data.file) {
        deleteFileIds.push(Number(selectedAttachment.id))
      }

      await updateTenant({
        id: String(currentTenant.id),
        data: {
          roomId,
          name: currentTenant.name,
          nameKana: currentTenant.nameKana,
          phone1: currentTenant.phone1,
          phone2: currentTenant.phone2,
          phone3: currentTenant.phone3,
          email: currentTenant.email,
          birthday: currentTenant.birthday ?? null,
          startDate: currentTenant.startDate,
          rent: currentTenant.rent,
          commonAreaFee: currentTenant.commonAreaFee,
          isParking: currentTenant.isParking,
          parkingFee: currentTenant.parkingFee,
          parkingNumber: currentTenant.parkingNumber ?? "",
          otherFees: currentTenant.otherFees ?? null,
          purpose: currentTenant.purpose,
          uploadFiles: uploadedFiles.length > 0 ? uploadedFiles : undefined,
          deleteFileIds: deleteFileIds.length > 0 ? deleteFileIds : undefined,
        },
      })
      toast.success(
        attachmentModalMode === "add" ? "添付ファイルを追加しました" : "添付ファイルを更新しました"
      )
      setAttachmentModalOpen(false)
      setSelectedAttachment(null)
    } catch (error) {
      console.error("Failed to upload attachment:", error)
    } finally {
      setIsUploadingAttachment(false)
    }
  }

  const handleDeleteAttachment = async () => {
    if (!selectedAttachment || !currentTenant || !roomId) {
      toast.error("添付ファイルの削除に必要な情報が不足しています")
      return
    }

    setIsUploadingAttachment(true)
    try {
      await updateTenant({
        id: String(currentTenant.id),
        data: {
          roomId,
          name: currentTenant.name,
          nameKana: currentTenant.nameKana,
          phone1: currentTenant.phone1,
          phone2: currentTenant.phone2,
          phone3: currentTenant.phone3,
          email: currentTenant.email,
          birthday: currentTenant.birthday ?? null,
          startDate: currentTenant.startDate,
          rent: currentTenant.rent,
          commonAreaFee: currentTenant.commonAreaFee,
          isParking: currentTenant.isParking,
          parkingFee: currentTenant.parkingFee,
          parkingNumber: currentTenant.parkingNumber ?? "",
          otherFees: currentTenant.otherFees ?? null,
          purpose: currentTenant.purpose,
          deleteFileIds: [Number(selectedAttachment.id)],
        },
      })
      toast.success("添付ファイルを削除しました")
      setAttachmentModalOpen(false)
      setSelectedAttachment(null)
    } catch (error) {
      console.error("Failed to delete attachment:", error)
    } finally {
      setIsUploadingAttachment(false)
    }
  }

  const handleRevivalTenant = () => {
    if (!currentTenant?.id) return
    reviveTenant({ id: currentTenant.id })
  }

  const handleDeleteTenant = () => {
    if (!currentTenant?.id) return
    deleteTenant({ id: currentTenant.id })
  }

  const handleDeletePlan = (planId: number) => {
    deletePlan({ id: planId })
  }

  const renderPlanInfo = (tenant: typeof activeTenant, hasNextTenant = false) => {
    const plan = roomLib.roomCurrentPlan(room, tenant)
    const nextPlan = roomLib.roomActivePlan(room, tenant)
      ? roomLib.roomPlannedPlan(room, tenant)
      : null

    return (
      <RoomPlanInfoCard
        plan={plan}
        nextPlan={nextPlan}
        onAdd={
          tenant
            ? async () => {
                if (await ensurePaymentMethod()) {
                  navigate(
                    `/properties/${propertyId}/rooms/${roomId}/plans/addPlan?tenantId=${tenant.id}`
                  )
                }
              }
            : () => undefined
        }
        onCancel={
          plan
            ? () => navigate(`/properties/${propertyId}/rooms/${roomId}/plans/${plan.id}/cancel`)
            : undefined
        }
        onRevival={plan ? () => revivePlan({ id: plan.id }) : undefined}
        onChangeEndDate={
          plan && hasNextTenant
            ? () => setEndDateChangeTarget({ type: "plan", value: plan })
            : undefined
        }
        onDelete={handleDeletePlan}
        isReviving={isPlanReviving}
        isDeleting={isPlanDeleting}
        disableAdd={!tenant}
        hasNextTenant={hasNextTenant}
      />
    )
  }

  const handleEndDateChange = (date: Date) => {
    if (!endDateChangeTarget) return

    const endDate = dateLib.formatDateISO(date)
    if (endDateChangeTarget.type === "tenant") {
      tenantEndDateChangeMutation.mutate({
        id: endDateChangeTarget.value.id,
        data: {
          endDate,
          endReason: endDateChangeTarget.value.endReason ?? undefined,
        },
      })
      return
    }

    planEndDateChangeMutation.mutate({
      id: endDateChangeTarget.value.id,
      data: {
        endDate,
        endReason: endDateChangeTarget.value.endReason ?? undefined,
      },
    })
  }

  if (!propertyId || !roomId) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="text-slate-600">物件IDまたは部屋IDが不正です。</div>
      </div>
    )
  }

  if (isRoomLoading) {
    return <Loading className="mt-10" size="lg" text="部屋情報を読み込んでいます..." />
  }

  if (roomError || !room) {
    interface ErrorWithDetails extends Error {
      status?: number
      type?: string
    }
    const errorData = roomError as ErrorWithDetails | undefined
    const is404 = errorData?.status === 404 || errorData?.type === "/not-found"
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <div className="text-slate-600">
          {is404 ? "指定された部屋が見つかりませんでした。" : "部屋情報の取得に失敗しました。"}
        </div>
        <Button onClick={() => navigate("/properties")} variant="outline">
          物件一覧に戻る
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <PropertyHeader propertyId={propertyId} />

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
        <TabsList className="grid w-full grid-cols-2 rounded-md bg-slate-100 p-1">
          <TabsTrigger
            value="details"
            className="w-full rounded px-4 py-1.5 text-sm font-semibold data-[state=active]:bg-white data-[state=active]:text-[#354267] data-[state=active]:shadow-sm"
          >
            部屋詳細
          </TabsTrigger>
          <TabsTrigger
            value="related"
            className="w-full rounded px-4 py-1.5 text-sm font-semibold data-[state=active]:bg-white data-[state=active]:text-[#354267] data-[state=active]:shadow-sm"
          >
            関連情報
          </TabsTrigger>
        </TabsList>

        <TabsContent value="details">
          <div className="space-y-5">
            <SummaryCard
              title="概要"
              icon={<BookOpen className="h-4 w-4 text-slate-400" />}
              action={
                <EchoesEditGuard>
                  <Link to={`/properties/${propertyId}/rooms/${roomId}/edit`}>
                    <Button variant="outline" size="sm" className="gap-2">
                      <Edit2 className="h-3.5 w-3.5" />
                      編集
                    </Button>
                  </Link>
                </EchoesEditGuard>
              }
              items={[
                { label: "部屋番号", value: room?.name ?? "-" },
                {
                  label: "用途",
                  value: tenantLib.getLabel(tenantType.TENANT_PURPOSES, currentTenant?.purpose),
                },
                { label: "ステータス", value: <TenantStatusBadge tenant={currentTenant} /> },
                { label: "階数", value: roomLib.roomFloorFormat(room) },
                { label: "間取り", value: room?.layout ?? "-" },
                { label: "面積", value: roomLib.roomSizeFormat(room) },
              ]}
              columns={2}
              contentClassName="p-3.5"
            />

            <RoomTenantInfoCard
              tenant={activeTenant}
              nextTenant={nextTenant}
              onEdit={
                activeTenant
                  ? () =>
                      navigate(
                        `/properties/${propertyId}/rooms/${roomId}/tenants/${activeTenant.id}/edit`
                      )
                  : undefined
              }
              onNextEdit={
                nextTenant
                  ? () =>
                      navigate(
                        `/properties/${propertyId}/rooms/${roomId}/tenants/${nextTenant.id}/edit`
                      )
                  : undefined
              }
              onAdd={() => navigate(`/properties/${propertyId}/rooms/${roomId}/tenants/add`)}
              onCancel={
                activeTenant
                  ? () =>
                      navigate(
                        `/properties/${propertyId}/rooms/${roomId}/tenants/${activeTenant.id}/cancel`
                      )
                  : undefined
              }
              onRevival={activeTenant ? handleRevivalTenant : undefined}
              onChangeEndDate={
                activeTenant && nextTenant
                  ? () => setEndDateChangeTarget({ type: "tenant", value: activeTenant })
                  : undefined
              }
              onDelete={activeTenant ? handleDeleteTenant : undefined}
              isReviving={isReviving}
              isDeleting={isDeleting}
              isRevivalDialogOpen={isRevivalDialogOpen}
              setIsRevivalDialogOpen={setIsRevivalDialogOpen}
              isDeleteDialogOpen={isDeleteDialogOpen}
              setIsDeleteDialogOpen={setIsDeleteDialogOpen}
              planContent={activeTenant ? renderPlanInfo(activeTenant, !!nextTenant) : undefined}
              nextPlanContent={nextTenant ? renderPlanInfo(nextTenant) : undefined}
            />
          </div>

          {activeTenant && (
            <>
              <hr className="my-7 border-slate-200" />

              <Suspense fallback={<Loading text="関連案件を読み込んでいます..." />}>
                <RoomRelatedCasesSection
                  propertyId={propertyId}
                  roomId={roomId}
                  tenantId={activeTenant.id}
                />
              </Suspense>
            </>
          )}

          <hr className="my-7 border-slate-200" />

          {/* Owner Memo Section - Header outside Suspense */}
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold text-[#354267]">オーナーメモ</h2>
              </div>
              <Button onClick={() => setShowAddMemoModal(true)} className="gap-1.5">
                <Plus className="h-3.5 w-3.5" />
                追加
              </Button>
            </div>

            <Card className="border border-slate-200 bg-slate-100">
              <CardContent className="p-3">
                <p className="text-xs font-semibold text-[#354267]">
                  カスタマーセンターの対応には影響しませんので、自身のメモとしてご活用ください
                </p>
              </CardContent>
            </Card>

            <Suspense fallback={<Loading text="オーナーメモを読み込んでいます..." />}>
              <OwnerMemoSectionContent
                propertyId={propertyId}
                roomId={roomId}
                showAddModal={showAddMemoModal}
                onShowAddModalChange={setShowAddMemoModal}
              />
            </Suspense>
          </section>
        </TabsContent>

        <TabsContent value="related">
          <RoomRelatedInfo
            room={room as unknown as RoomRes}
            onAddTenant={() => {
              navigate(`/properties/${propertyId}/rooms/${roomId}/tenants/add`)
            }}
            onEditFee={
              currentTenant
                ? () => {
                    navigate(
                      `/properties/${propertyId}/rooms/${roomId}/tenants/${currentTenant.id}/edit`
                    )
                  }
                : undefined
            }
            onAddAttachment={handleAddAttachment}
            onViewAttachmentDetail={handleEditAttachment}
          />
        </TabsContent>
      </Tabs>

      <AttachmentModal
        open={attachmentModalOpen}
        onOpenChange={setAttachmentModalOpen}
        mode={attachmentModalMode}
        fileIndex={selectedAttachment?.index}
        initialData={
          selectedAttachment
            ? {
                title: selectedAttachment.title,
                imageUrl: selectedAttachment.imageUrl,
                fileUrl: selectedAttachment.imageUrl,
                fileName: selectedAttachment.fileName,
                fileType: selectedAttachment.fileType,
              }
            : undefined
        }
        onSubmit={handleAttachmentSubmit}
        onDelete={attachmentModalMode === "edit" ? handleDeleteAttachment : undefined}
        isSubmitting={isUploadingAttachment}
      />
      <PaymentMethodRequiredDialog
        open={isPaymentMethodDialogOpen}
        onOpenChange={setIsPaymentMethodDialogOpen}
      />

      {endDateChangeTarget?.value.endDate && nextTenant && (
        <EndDateChangeDialog
          type={endDateChangeTarget.type}
          open
          initialDate={endDateChangeTarget.value.endDate}
          nextTenantStartDate={nextTenant.startDate}
          isSubmitting={
            tenantEndDateChangeMutation.isPending || planEndDateChangeMutation.isPending
          }
          onOpenChange={(open) => !open && setEndDateChangeTarget(null)}
          onSubmit={handleEndDateChange}
        />
      )}
    </div>
  )
}
